import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sidebar izquierda: lista de conversaciones del usuario. Modelo nuevo: cada
 * row es un chat 1-a-1 con `getOtherParticipantName(conv)`.
 *
 * Props:
 *   conversations          – array de conversation objects
 *   activeConv             – conv seleccionada (o null)
 *   getOtherParticipantName – (conv) => string
 *   onSelectConversation   – (conv) => void
 *   formatDate             – (dateString) => string
 */
const ConversationList = ({
  conversations,
  activeConv,
  getOtherParticipantName,
  onSelectConversation,
  formatDate,
}) => {
  const { t } = useTranslation();

  return (
    <div className="cm-chat-conv-list overflow-y-auto bg-[var(--surface)] w-full tablet-sm:max-w-[320px] tablet-sm:min-w-[240px]">
      <div className="cm-chat-conv-header px-4 py-4">
        <h1 className="text-2xl font-black text-on-surface tracking-tight">{t('Conversaciones')}</h1>
      </div>

      {conversations.length === 0 ? (
        <div className="p-6 text-center">
          <div
            className="mx-auto mb-3 w-14 h-14 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'rgb(var(--cm-primary) / 0.08)' }}
          >
            <span
              className="material-symbols-outlined text-[28px]"
              style={{ color: 'rgb(var(--cm-primary))' }}
            >
              chat_bubble_outline
            </span>
          </div>
          <p className="text-sm font-semibold text-on-surface mb-1">
            {t('Aún sin conversaciones')}
          </p>
          <p className="text-xs text-on-surface-variant leading-relaxed">
            {t('Cuando contactes a un vendedor o reciban un mensaje, va a aparecer acá.')}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-outline-variant/20">
          {conversations.map((conv) => {
            const isSelected = activeConv?.id === conv.id;
            const accent = 'rgb(var(--cm-primary))';
            const accentBg = 'rgb(var(--cm-primary) / 0.1)';
            const otherName = getOtherParticipantName(conv);

            return (
              <li key={conv.id}>
                <button
                  type="button"
                  onClick={() => onSelectConversation(conv)}
                  className="w-full text-left flex items-start gap-3 px-3 py-2.5 transition-colors"
                  style={{
                    borderLeft: `3px solid ${isSelected ? accent : `${accent}66`}`,
                    backgroundColor: isSelected ? accentBg : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = `${accent}0a`; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div className="relative shrink-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: accent }}
                    >
                      {otherName.charAt(0).toUpperCase()}
                    </div>
                    {conv.hasUnread && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-white"
                        style={{ backgroundColor: '#ef4444' }}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-sm truncate ${conv.hasUnread ? 'font-semibold text-on-surface' : 'font-normal text-on-surface'}`}>
                        {otherName}
                      </span>
                    </div>

                    <p className={`text-[11px] truncate ${conv.hasUnread ? 'font-medium text-on-surface' : 'text-on-surface-variant'}`}>
                      {conv.lastMessage || t('Sin mensajes')}
                    </p>
                  </div>

                  <span className="text-[10px] text-on-surface-variant shrink-0">
                    {formatDate(conv.updatedAt)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ConversationList;
